import { useEffect, useState } from 'react';
import {
  Box,
  Table,
  TableCell,
  TableHead,
  TableBody,
  Typography,
  TableRow,
  IconButton,
  styled,
  TableContainer,
} from '@mui/material';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { InfoOutlined } from '@mui/icons-material';

import { FCheckbox, FHAntdDateTimePicker, FHButton, FHTooltip } from '../../../../../../base';
import {
  FORTA_BLUE,
  FORTA_ERROR,
  FORTA_GRAY3,
  FORTA_GRAY4,
  FORTA_ORANGE,
  FORTA_WHITE,
} from '../../../../../../../utils/colors';
import TrashIcon from '../../../../../../svgs/TrashIcon3';
import { goalActions } from '../../../../../../../store';
import { API_DATE_FORMAT } from '../../../../../../../constants';
import { disabledDatesOutsideRange } from '../../../../../../../helpers';
import { GoalTypes, TreatmentPlanResourceVersion } from '../../constants/general';
import {
  FIRST_HYPOTHESIZED_FUNC__DATE_MESSAGE_V1,
  FIRST_HYPOTHESIZED_FUNC__DATE_MESSAGE_V2,
} from '../baselineAndMasteryCriteria/components/common/constants/general';
import { isAtLeastOneChecked, areRowValuesSame, isPostCallAllowed } from './utils/general';

import type { IHypothesizedFunction } from '../../../../../../../utils/types';
import type { Moment } from 'moment';

export interface HypoFunctionRowState {
  date_selected: Moment | null | string;
  is_attention: boolean;
  is_escape_or_avoidance: boolean;
  is_sensory: boolean;
  is_tangible: boolean;
  date_edits_blocked: string | null;
}

const initialStateHypoFunctionRowState = {
  date_selected: null,
  is_attention: false,
  is_escape_or_avoidance: false,
  is_sensory: false,
  is_tangible: false,
  date_edits_blocked: null,
};

interface HypothesizedFunctionProps {
  goalId?: number;
  goalIndex?: number;
  version?: TreatmentPlanResourceVersion;
  treatmentPlanId: number;
  isEditable: boolean;
  hypothesizedFunctions: IHypothesizedFunction[];
}

interface HypothesizedFunctionRowProps {
  version?: TreatmentPlanResourceVersion;
  isFirstRow?: boolean;
  isLastRow?: boolean;
  isEditable?: boolean;
  goalId?: number;
  goalIndex?: number;
  loading: boolean;
  treatmentPlanId: number;
  secondLastRowData: IHypothesizedFunction | null;
  hypothesizedFunction?: IHypothesizedFunction;
  onDelete: (id: number | undefined) => void;
}

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

function TableHeader() {
  return (
    <TableHead
      sx={{
        backgroundColor: FORTA_BLUE.light3,
        borderTopLeftRadius: '16px !important',
        borderTopRightRadius: '16px !important',
        overflow: 'hidden',
      }}
    >
      <StyledTableRow>
        <TableCell
          align="center"
          sx={{
            p: 1.2,
            borderTopLeftRadius: '16px !important',
            overflow: 'hidden',
            width: '5%',
          }}
        ></TableCell>
        <TableCell
          sx={{
            p: 1.2,
            color: FORTA_BLUE.primary,
            fontWeight: 700,
            width: '15%',
            borderRight: `1px solid ${FORTA_GRAY3} !important`,
          }}
          align="center"
        >
          Date
        </TableCell>
        <TableCell
          sx={{
            p: 1.2,
            color: FORTA_BLUE.primary,
            fontWeight: 700,
            width: '20%',
            borderRight: `1px solid ${FORTA_GRAY3} !important`,
          }}
          align="center"
        >
          Sensory
        </TableCell>
        <TableCell
          sx={{
            p: 1.2,
            color: FORTA_BLUE.primary,
            fontWeight: 700,
            width: '20%',
            borderRight: `1px solid ${FORTA_GRAY3} !important`,
          }}
          align="center"
        >
          Escape/avoidance
        </TableCell>
        <TableCell
          sx={{
            p: 1.2,
            color: FORTA_BLUE.primary,
            fontWeight: 700,
            width: '20%',
            borderRight: `1px solid ${FORTA_GRAY3} !important`,
          }}
          align="center"
        >
          Attention
        </TableCell>
        <TableCell
          sx={{
            p: 1.2,
            color: FORTA_BLUE.primary,
            fontWeight: 700,
            borderTopRightRadius: '16px !important',
            overflow: 'hidden',
            width: '20%',
          }}
          align="center"
        >
          Tangible
        </TableCell>
      </StyledTableRow>
    </TableHead>
  );
}

function HypothesizedFunctionRow({
  version,
  isFirstRow,
  isLastRow,
  goalId,
  goalIndex,
  loading,
  secondLastRowData,
  treatmentPlanId,
  isEditable,
  hypothesizedFunction,
  onDelete,
}: HypothesizedFunctionRowProps) {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const [rowState, setRowState] = useState<HypoFunctionRowState>(initialStateHypoFunctionRowState);
  const [hightLightCheckboxes, setHighLightCheckboxes] = useState<boolean>(
    () => !hypothesizedFunction || !isAtLeastOneChecked(hypothesizedFunction),
  );

  const isRowEditable = isEditable && !hypothesizedFunction?.date_edits_blocked;

  const dateMessage =
    version === TreatmentPlanResourceVersion.V1
      ? FIRST_HYPOTHESIZED_FUNC__DATE_MESSAGE_V1
      : FIRST_HYPOTHESIZED_FUNC__DATE_MESSAGE_V2;

  useEffect(() => {
    if (!hypothesizedFunction) return;
    const {
      date_selected,
      is_attention,
      is_escape_or_avoidance,
      is_sensory,
      is_tangible,
      date_edits_blocked,
    } = hypothesizedFunction;
    setRowState({
      date_selected: date_selected
        ? date_selected === dateMessage
          ? date_selected
          : moment(date_selected)
        : null,
      is_attention,
      is_escape_or_avoidance,
      is_sensory,
      is_tangible,
      date_edits_blocked,
    });
  }, [hypothesizedFunction]);

  const handleFieldUpdate = async (updatedState: HypoFunctionRowState) => {
    const data = {
      date_selected: updatedState.date_selected
        ? moment(updatedState.date_selected).format(API_DATE_FORMAT)
        : null,
      is_attention: updatedState.is_attention,
      is_escape_or_avoidance: updatedState.is_escape_or_avoidance,
      is_sensory: updatedState.is_sensory,
      is_tangible: updatedState.is_tangible,
    };

    if (hypothesizedFunction?.id) {
      const isSameAsPreviousRow = secondLastRowData
        ? areRowValuesSame(updatedState, secondLastRowData)
        : false;

      const isSameAsSavedRow = areRowValuesSame(updatedState, hypothesizedFunction);

      if (isSameAsPreviousRow) {
        setRowState(updatedState);
        setHighLightCheckboxes(true);
        return;
      }

      if (isSameAsSavedRow) {
        setRowState(updatedState);
        setHighLightCheckboxes(false);
        return;
      }

      setHighLightCheckboxes(false);

      const response = await dispatch(
        goalActions.updateHypothesizedFunctionRecord({
          treatmentPlanId,
          goalId,
          getAccessTokenSilently,
          data,
        }),
      );
      if (!response.error) {
        setRowState(updatedState);
      }
    } else {
      const isCreateRowAllowed = isPostCallAllowed(
        updatedState,
        secondLastRowData,
        Boolean(isLastRow),
        data.date_selected,
      );
      setRowState(updatedState);

      if (!isCreateRowAllowed || !goalId) {
        setHighLightCheckboxes(true);

        // Include hypothesized function value in redux if goal isn't saved yet
        if (isFirstRow) {
          dispatch(
            goalActions.updateGoalProperty({
              goalIndex,
              goalType: GoalTypes.BehaviorReduction,
              key: 'hypothesized_functions',
              value: [
                {
                  date_selected: dateMessage,
                  is_attention: updatedState.is_attention,
                  is_escape_or_avoidance: updatedState.is_escape_or_avoidance,
                  is_sensory: updatedState.is_sensory,
                  is_tangible: updatedState.is_tangible,
                  date_edits_blocked: null,
                },
              ],
            }),
          );
          if (isAtLeastOneChecked(updatedState)) {
            setHighLightCheckboxes(false);
          }
        }
        return;
      }
      setHighLightCheckboxes(false);
      dispatch(
        goalActions.createHypothesizedFunctionRecord({
          treatmentPlanId,
          goalId,
          getAccessTokenSilently,
          data,
        }),
      );
    }
  };

  let numberOfDaysFromNowToDisable = -10000;
  if (secondLastRowData) {
    numberOfDaysFromNowToDisable = -moment().diff(secondLastRowData.date_selected, 'days') + 1;
  }

  return (
    <>
      <StyledTableRow sx={{ borderRadius: '16px' }}>
        <TableCell
          sx={{
            p: 1,
            width: '5%',
            borderTop: `1px solid ${FORTA_GRAY3} !important`,
          }}
          align="center"
        >
          {isRowEditable && !isFirstRow && (
            <IconButton
              disabled={!isLastRow || loading}
              sx={{ opacity: isLastRow ? 1 : 0.3 }}
              onClick={() => {
                onDelete(hypothesizedFunction?.id);
              }}
            >
              <TrashIcon width={18} height={18} />
            </IconButton>
          )}
        </TableCell>
        <TableCell
          sx={{
            p: 1,
            width: '15%',
            borderRight: `1px solid ${FORTA_GRAY3} !important`,
            borderTop: `1px solid ${FORTA_GRAY3} !important`,
          }}
          align="center"
        >
          {rowState.date_selected === dateMessage || (isFirstRow && !goalId) ? (
            <Typography fontSize={14} color={FORTA_BLUE.primary}>
              {dateMessage}
            </Typography>
          ) : isRowEditable && !isFirstRow ? (
            <FHAntdDateTimePicker
              format="MM/DD/YY"
              allowClear={false}
              inputHeight={40}
              style={{ width: 160, boxShadow: 'none' }}
              coloredBackground
              disabled={!isLastRow || loading}
              disabledDate={(current: Moment) =>
                disabledDatesOutsideRange(current, numberOfDaysFromNowToDisable, 0)
              }
              value={rowState.date_selected}
              onChange={(value: any) => {
                handleFieldUpdate({
                  ...rowState,
                  date_selected: value ? value : null,
                });
              }}
              error={!rowState.date_selected}
            />
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Typography
                sx={{ color: FORTA_BLUE.primary, fontWeight: 700, mr: 1, ml: isFirstRow ? 3 : 0 }}
              >
                {rowState.date_selected ? moment(rowState.date_selected).format('MM/DD/YY') : ''}
              </Typography>
              {isFirstRow && (
                <FHTooltip
                  arrow
                  title={
                    <Typography>
                      {version === TreatmentPlanResourceVersion.V1
                        ? FIRST_HYPOTHESIZED_FUNC__DATE_MESSAGE_V1
                        : FIRST_HYPOTHESIZED_FUNC__DATE_MESSAGE_V2}
                    </Typography>
                  }
                  placement="top"
                  sx={{
                    borderRadius: '10px',
                    display: 'flex',
                    maxWidth: '350px',
                  }}
                  tooltipbackground={FORTA_WHITE}
                  tooltipBoxShadow={'0px 4px 4px rgba(0, 0, 0, 0.25)'}
                >
                  <InfoOutlined sx={{ color: FORTA_ORANGE }} />
                </FHTooltip>
              )}
            </Box>
          )}
        </TableCell>
        <TableCell
          sx={{
            p: 1,
            width: '20%',
            borderRight: `1px solid ${FORTA_GRAY3} !important`,
            borderTop: `1px solid ${FORTA_GRAY3} !important`,
          }}
          align="center"
        >
          <FCheckbox
            iconStyles={{
              boxShadow: isRowEditable && isLastRow ? '0px 4px 4px rgba(0, 0, 0, 0.25)' : 'none',
              width: '25px',
              height: '25px',
              border:
                hightLightCheckboxes && !rowState.is_sensory
                  ? `2px solid ${FORTA_ERROR}`
                  : `1px solid ${FORTA_GRAY3}`,
            }}
            sx={{ cursor: isRowEditable && isLastRow ? 'pointer' : 'not-allowed !important' }}
            disabled={!isRowEditable || !isLastRow || loading}
            checked={rowState.is_sensory}
            data-testid={`sensory-${hypothesizedFunction?.id ? hypothesizedFunction?.id : 'new'}`}
            onChange={(e) => {
              handleFieldUpdate({
                ...rowState,
                is_sensory: e.target.checked,
              });
            }}
            coloredBackground
          />
        </TableCell>
        <TableCell
          sx={{
            p: 1,
            width: '20%',
            borderRight: `1px solid ${FORTA_GRAY3} !important`,
            borderTop: `1px solid ${FORTA_GRAY3} !important`,
          }}
          align="center"
        >
          <FCheckbox
            iconStyles={{
              boxShadow: isRowEditable && isLastRow ? '0px 4px 4px rgba(0, 0, 0, 0.25)' : 'none',
              width: '25px',
              height: '25px',
              border:
                hightLightCheckboxes && !rowState.is_escape_or_avoidance
                  ? `2px solid ${FORTA_ERROR}`
                  : `1px solid ${FORTA_GRAY3}`,
            }}
            sx={{ cursor: isRowEditable && isLastRow ? 'pointer' : 'not-allowed !important' }}
            disabled={!isRowEditable || !isLastRow || loading}
            checked={rowState.is_escape_or_avoidance}
            data-testid={`is_escape_or_avoidance-${
              hypothesizedFunction?.id ? hypothesizedFunction?.id : 'new'
            }`}
            onChange={(e) => {
              handleFieldUpdate({
                ...rowState,
                is_escape_or_avoidance: e.target.checked,
              });
            }}
            coloredBackground
          />
        </TableCell>
        <TableCell
          sx={{
            p: 1,
            width: '20%',
            borderRight: `1px solid ${FORTA_GRAY3} !important`,
            borderTop: `1px solid ${FORTA_GRAY3} !important`,
          }}
          align="center"
        >
          <FCheckbox
            iconStyles={{
              boxShadow: isRowEditable && isLastRow ? '0px 4px 4px rgba(0, 0, 0, 0.25)' : 'none',
              width: '25px',
              height: '25px',
              border:
                hightLightCheckboxes && !rowState.is_attention
                  ? `2px solid ${FORTA_ERROR}`
                  : `1px solid ${FORTA_GRAY3}`,
            }}
            sx={{ cursor: isRowEditable && isLastRow ? 'pointer' : 'not-allowed !important' }}
            disabled={!isRowEditable || !isLastRow || loading}
            data-testid={`is_attention-${
              hypothesizedFunction?.id ? hypothesizedFunction?.id : 'new'
            }`}
            checked={rowState.is_attention}
            onChange={(e) => {
              handleFieldUpdate({
                ...rowState,
                is_attention: e.target.checked,
              });
            }}
            coloredBackground
          />
        </TableCell>
        <TableCell
          sx={{
            p: 1,
            width: '20%',
            borderTop: `1px solid ${FORTA_GRAY3} !important`,
          }}
          align="center"
        >
          <FCheckbox
            iconStyles={{
              boxShadow: isRowEditable && isLastRow ? '0px 4px 4px rgba(0, 0, 0, 0.25)' : 'none',
              width: '25px',
              height: '25px',
              border:
                hightLightCheckboxes && !rowState.is_tangible
                  ? `2px solid ${FORTA_ERROR}`
                  : `1px solid ${FORTA_GRAY3}`,
            }}
            sx={{ cursor: isRowEditable && isLastRow ? 'pointer' : 'not-allowed !important' }}
            checked={rowState.is_tangible}
            disabled={!isRowEditable || !isLastRow || loading}
            data-testid={`is_tangible-${
              hypothesizedFunction?.id ? hypothesizedFunction?.id : 'new'
            }`}
            onChange={(e) => {
              handleFieldUpdate({
                ...rowState,
                is_tangible: e.target.checked,
              });
            }}
            coloredBackground
          />
        </TableCell>
      </StyledTableRow>
      {hightLightCheckboxes && (
        <StyledTableRow>
          <TableCell colSpan={6}>
            <Typography color={FORTA_ERROR} fontWeight={700}>
              {`To save the row, ${
                !rowState.date_selected ? 'please select date and ' : 'please select '
              } at least one checkbox and a different combination of
              checkboxes from the previous row.`}
            </Typography>
          </TableCell>
        </StyledTableRow>
      )}
    </>
  );
}

function HypothesizedFunction({
  goalIndex,
  treatmentPlanId,
  goalId,
  version,
  isEditable,
  hypothesizedFunctions,
}: HypothesizedFunctionProps) {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const [addNewRow, setAddNewRow] = useState<boolean>(false);
  const [currentHypoFunctionLength, setCurrentHypoFunctionLength] = useState<number>(
    hypothesizedFunctions.length,
  );

  const { loading } = useSelector((state: any) => state.goals);

  const disableUpdateFunction = hypothesizedFunctions.length
    ? moment(hypothesizedFunctions[hypothesizedFunctions.length - 1].date_selected).isSameOrAfter(
        moment().subtract(1, 'day'),
      )
    : false;

  // This is done to immediately reflect change on UI instead of waiting for addNewRow state to reflect
  let hideNewRow = false;
  if (hypothesizedFunctions.length > currentHypoFunctionLength) {
    hideNewRow = true;
  }

  useEffect(() => {
    if (hypothesizedFunctions.length > currentHypoFunctionLength) {
      setAddNewRow(false);
    }
    if (hypothesizedFunctions.length === 0) {
      setAddNewRow(true);
    }
    setCurrentHypoFunctionLength(hypothesizedFunctions.length);
  }, [hypothesizedFunctions]);

  useEffect(() => {
    if (!isEditable && hypothesizedFunctions.length > 0) {
      setAddNewRow(false);
    }
  }, [isEditable, hypothesizedFunctions]);

  const secondLastRowData = addNewRow
    ? hypothesizedFunctions.length - 1 >= 0
      ? hypothesizedFunctions[hypothesizedFunctions.length - 1]
      : null
    : hypothesizedFunctions.length - 2 >= 0
      ? hypothesizedFunctions[hypothesizedFunctions.length - 2]
      : null;

  const handleRemoveRow = (id: number | undefined) => {
    if (!id) {
      setAddNewRow(false);
    } else {
      dispatch(
        goalActions.deleteHypothesizedFunctionRecord({
          getAccessTokenSilently,
          treatmentPlanId,
          goalId,
        }),
      );
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography
            fontWeight={'bold'}
            textAlign={'left'}
            color={hypothesizedFunctions.length === 0 ? FORTA_ERROR : FORTA_GRAY4}
          >
            Hypothesized function*
          </Typography>
          {hypothesizedFunctions.length === 0 && (
            <Typography color={FORTA_ERROR}>At least one row is required</Typography>
          )}
        </Box>
        {isEditable && goalId && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FHButton
              startIcon={<AddCircleOutlineIcon sx={{ width: 24, height: 24 }} />}
              onClick={() => {
                setAddNewRow(true);
              }}
              disabled={addNewRow || loading || disableUpdateFunction}
            >
              Update function
            </FHButton>
            {disableUpdateFunction && (
              <FHTooltip
                arrow
                title={
                  <Typography>
                    {`Updates can be made after ${moment(
                      hypothesizedFunctions[hypothesizedFunctions.length - 1].date_selected,
                    ).format('Do MMM yy')}`}
                  </Typography>
                }
                placement="top"
                sx={{
                  borderRadius: '10px',
                  display: 'flex',
                  maxWidth: '350px',
                }}
                tooltipbackground={FORTA_WHITE}
                tooltipBoxShadow={'0px 4px 4px rgba(0, 0, 0, 0.25)'}
              >
                <InfoOutlined sx={{ color: FORTA_ORANGE, ml: 1 }} />
              </FHTooltip>
            )}
          </Box>
        )}
      </Box>
      <TableContainer
        sx={{
          mt: 2,
          borderRadius: '16px',
          overflow: 'hidden',
          border: `1px solid ${FORTA_GRAY3}`,
        }}
      >
        <Table sx={{ borderRadius: '16px' }}>
          <TableHeader />
          <TableBody>
            {hypothesizedFunctions.map((hf, index) => {
              return (
                <HypothesizedFunctionRow
                  loading={loading}
                  treatmentPlanId={treatmentPlanId}
                  goalId={goalId}
                  goalIndex={goalIndex}
                  hypothesizedFunction={hf}
                  version={version}
                  key={hf.id}
                  onDelete={handleRemoveRow}
                  isEditable={isEditable}
                  isFirstRow={index === 0}
                  isLastRow={index === hypothesizedFunctions.length - 1 && !addNewRow}
                  secondLastRowData={secondLastRowData}
                />
              );
            })}
            {addNewRow && !hideNewRow && (
              <HypothesizedFunctionRow
                loading={loading}
                goalIndex={goalIndex}
                onDelete={handleRemoveRow}
                isEditable
                version={version}
                treatmentPlanId={treatmentPlanId}
                goalId={goalId}
                isFirstRow={hypothesizedFunctions.length === 0}
                isLastRow
                secondLastRowData={secondLastRowData}
              />
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default HypothesizedFunction;
